import { cssJoin } from '@volvo-cars/css/utils';
import { IconButton } from '@volvo-cars/react-icons';
import type React from 'react';
import { forwardRef, useState } from 'react';
import { TextLikeInput } from './text-input';
import type { TextLikeProps } from './types';
import { useIds } from './use-ids';

export interface PasswordInputProps extends TextLikeProps {
  /**
   * Password is initially visible.
   *
   * @default false
   */
  defaultPasswordVisible?: boolean;

  /**
   * Pass `current-password` to autocomplete the users current password or
   * `new-password` to autocomplete the new password in a change password form.
   */
  autoComplete?: 'current-password' | 'new-password' | 'off';

  /**
   * Regular expression that the password must match.
   */
  pattern?: string;

  /**
   * Specifies the visible width, in characters, of the input.
   */
  size?: number;

  /**
   * Minimum length of the password.
   */
  minLength?: number;

  /**
   * Maximum length of the password.
   */
  maxLength?: number;

  /**
   * Label for the show password button.
   */
  showPasswordLabel?: string;

  /**
   * Default value of an uncontrolled input.
   */
  defaultValue?: string;

  /**
   * Value of the input.
   *
   * Makes the input controlled.
   */
  value?: string;

  /**
   * Fires immediately when the input’s value is changed by the user (for example, it fires on every keystroke).
   */
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
}

/**
 * PasswordInput should be used when user enters a password.
 * It lets the user show or hide the contents of the input.
 */
export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  function PasswordInput(
    {
      variant = 'top-label',
      id,
      defaultPasswordVisible = false,
      showPasswordLabel,
      ...props
    }: PasswordInputProps,
    ref: React.ForwardedRef<HTMLInputElement>,
  ) {
    const [passwordVisible, setPasswordVisible] = useState(
      defaultPasswordVisible,
    );
    const { inputId, rootId } = useIds(id);
    return (
      <TextLikeInput
        {...props}
        variant={variant}
        type={passwordVisible ? 'text' : 'password'}
        id={rootId}
        spellCheck={false}
        autoCorrect="off"
        autoCapitalize="none"
        ref={ref}
        contentAfter={
          <IconButton
            icon={passwordVisible ? 'eye' : 'eye-slashed'}
            className={cssJoin('ml-0', variant === 'floating-label' && '-mr-8')}
            bleed={variant === 'top-label'}
            variant="clear"
            disabled={props.disabled}
            tabIndex={props.disabled || !showPasswordLabel ? -1 : undefined}
            {...(showPasswordLabel
              ? {
                  'aria-pressed': passwordVisible,
                  'aria-label': showPasswordLabel,
                  'aria-controls': inputId,
                }
              : {
                  'aria-label': '',
                  'aria-hidden': true,
                })}
            onClick={() => setPasswordVisible((state) => !state)}
          />
        }
      />
    );
  },
);
